import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { cubicOut } from 'svelte/easing';
import type { TransitionConfig } from 'svelte/transition';
import type { BusinessPermission, UserInfo } from './types/api/UserInfo';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

type FlyAndScaleParams = {
  y?: number;
  x?: number;
  start?: number;
  duration?: number;
};

export const flyAndScale = (
  node: Element,
  params: FlyAndScaleParams = { y: -8, x: 0, start: 0.95, duration: 150 }
): TransitionConfig => {
  const style = getComputedStyle(node);
  const transform = style.transform === 'none' ? '' : style.transform;

  const scaleConversion = (valueA: number, scaleA: [number, number], scaleB: [number, number]) => {
    const [minA, maxA] = scaleA;
    const [minB, maxB] = scaleB;

    const percentage = (valueA - minA) / (maxA - minA);
    const valueB = percentage * (maxB - minB) + minB;

    return valueB;
  };

  const styleToString = (style: Record<string, number | string | undefined>): string => {
    return Object.keys(style).reduce((str, key) => {
      if (style[key] === undefined) return str;
      return str + `${key}:${style[key]};`;
    }, '');
  };

  return {
    duration: params.duration ?? 200,
    delay: 0,
    css: (t) => {
      const y = scaleConversion(t, [0, 1], [params.y ?? 5, 0]);
      const x = scaleConversion(t, [0, 1], [params.x ?? 0, 0]);
      const scale = scaleConversion(t, [0, 1], [params.start ?? 0.95, 1]);

      return styleToString({
        transform: `${transform} translate3d(${x}px, ${y}px, 0) scale(${scale})`,
        opacity: t
      });
    },
    easing: cubicOut
  };
};

const pluralRules = new Intl.PluralRules('en-US');

export function getPluralText(count: number, singular: string, plural: string) {
  const rule = pluralRules.select(count);
  const messages: Record<Intl.LDMLPluralRule, string> = {
    zero: `${count} ${plural}`,
    one: `${count} ${singular}`,
    two: `${count} ${plural}`,
    few: `${count} ${plural}`,
    many: `${count} ${plural}`,
    other: `${count} ${plural}`
  };
  return messages[rule];
}

/**
 * Gets id of the business from either business or old capper model.
 */
export function getBusinessId(userInfo?: UserInfo | null): number | undefined {
  return userInfo?.business_user ? userInfo?.business_user.business.id : userInfo?.id;
}

/**
 * Gets name of the business from either business or old capper model.
 */
export function getBusinessName(userInfo?: UserInfo | null): string {
  return userInfo?.business_user ? userInfo?.business_user.business.name : userInfo?.username || '';
}

/**
 * Check if user contains all the specified business permissions.
 * Starts with checking if user is a 'classic' capper.
 */
export function hasPermissions(
  userInfo: UserInfo | null | undefined,
  permissions: BusinessPermission[]
): boolean {
  if (userInfo?.profile.is_capper) {
    return true;
  }

  if (!userInfo?.business_user?.role?.permissions) {
    return false;
  }

  return permissions.every((permission) =>
    userInfo.business_user.role.permissions.includes(permission)
  );
}
